import { useState, useRef, useEffect } from "react";
import { CATEGORIES, SORT_OPTIONS } from "../../../mocks/products";

interface FilterBarProps {
  category: string;
  sort: string;
  search: string;
  onCategoryChange: (c: string) => void;
  onSortChange: (s: string) => void;
  onSearchChange: (s: string) => void;
}

export default function FilterBar({
  category,
  sort,
  search,
  onCategoryChange,
  onSortChange,
  onSearchChange,
}: FilterBarProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const currentSort = SORT_OPTIONS.find((o) => o.id === sort);

  return (
    <div className="sticky top-16 z-30 bg-[#FAFAFA]/95 backdrop-blur border-b border-zinc-200 py-4">
      <div className="max-w-7xl mx-auto px-6">
        {/* Top row: Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          {/* Search */}
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="搜尋商品名稱或品牌..."
              className="w-full h-10 pl-9 pr-9 bg-transparent border-b border-zinc-300 focus:border-[#1B4332] focus:outline-none text-sm text-[#1B4332] placeholder:text-zinc-400 transition-colors duration-200"
            />
            {search && (
              <button
                onClick={() => onSearchChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-zinc-400 hover:text-zinc-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>

          {/* Sort dropdown */}
          <div className="relative w-full sm:w-52" ref={sortRef}>
            <button
              onClick={() => setSortOpen((v) => !v)}
              className="w-full h-10 flex items-center justify-between gap-2 border-b border-zinc-300 hover:border-[#1B4332] text-sm text-[#1B4332] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <span>{currentSort?.label}</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${sortOpen ? "rotate-180" : ""}`}></i>
            </button>
            {sortOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden z-10">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => { onSortChange(opt.id); setSortOpen(false); }}
                    className={`w-full px-4 h-10 text-left text-sm transition-colors duration-150 cursor-pointer ${
                      sort === opt.id
                        ? "bg-[#1B4332] text-white"
                        : "text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category tags */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => onCategoryChange(cat.id)}
              className={`h-9 px-4 text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                category === cat.id
                  ? "bg-[#1B4332] text-white"
                  : "bg-transparent border border-zinc-300 text-zinc-600 hover:border-zinc-600 hover:text-zinc-800"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
